/* eslint-disable */

// TODO: @adamsiwiec1 i will type this

import { apiClient } from '@/core/api/apiClient'
import { EXPIRATION_TIME_MS } from '@/constants'
import { FirebaseError } from '@firebase/app'
import {
  MultiFactorError,
  User,
  applyActionCode,
  confirmPasswordReset,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailLink,
  signOut,
  verifyPasswordResetCode
} from '@firebase/auth'
import HyperDX from '@hyperdx/browser'
import { LoginCredentials, PrivateUserInfoType } from '@otw/models'
import { auth } from 'config/firebase'
import { getAuthUserProfile } from 'core/auth/getAuthUserProfile'
import { handleLoginWithEmail } from 'core/auth/handleLoginWithEmail'
import { completeGoogleRedirectIfPresent, handleLoginWithGoogle } from 'core/auth/handleLoginWithGoogle'
import { writeAuthSessionHint } from 'components/auth/authSessionHint'
import React, { ReactNode, createContext, useCallback, useEffect, useState } from 'react'
// TODO: Doing this as i type ill probably finish https://firebase.google.com/docs/auth/custom-email-handler#create_the_email_action_handler_page

type IAuthContext = {
  // User state
  currentUser?: User | null
  userInfo?: PrivateUserInfoType
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  refreshUserInfo: (currentUser: User) => Promise<void>

  // Auth methods
  loginWithEmail: (credentials: LoginCredentials) => Promise<MultiFactorError | FirebaseError | void>
  loginWithGoogle: () => Promise<MultiFactorError | FirebaseError | void>
  googleAuthError: unknown
  logout: () => Promise<void>

  // Email verification
  handleResendVerificationEmail: (email: string, turnstileToken: string) => Promise<boolean>
  handleVerifyActionCode: (code: string) => Promise<boolean>
  handleSignInWithEmailLink: (code: string) => Promise<boolean>

  // Password reset
  handleSendPasswordResetEmail: (email: string) => Promise<void>
  handleVerifyPasswordResetCode: (code: string) => Promise<string>
  handleConfirmPasswordReset: (code: string, newPassword: string) => Promise<boolean>
  remainingSessionTime: number | null
}

type IAuthProviderProps = {
  children: ReactNode
}

const AuthContext = createContext<IAuthContext | null>(null)

const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userInfo, setUserInfo] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [remainingSessionTime, setRemainingSessionTime] = useState<number | null>(null)
  const [googleAuthError, setGoogleAuthError] = useState<unknown>(null)

  const loginWithEmail = async (credentials: LoginCredentials) => {
    await handleLoginWithEmail(credentials.email, credentials.password, setUserInfo)
  }

  const loginWithGoogle = async () => {
    await handleLoginWithGoogle(setUserInfo)
  }

  const logout = useCallback(async () => {
    // Clear session timer before signOut so other tabs / remounts
    // cannot resume a stale countdown and force-logout a fresh session.
    setRemainingSessionTime(null)
    writeAuthSessionHint(false)
    return signOut(auth)
  }, [])

  const handleResendVerificationEmail = async (email: string, turnstileToken: string) => {
    try {
      await apiClient.portal.public.resendVerificationEmail(email, turnstileToken)
      return true
    } catch (error) {
      console.error('Error resending verification email:', error)
      return false
    }
  }

  const handleVerifyActionCode = async (code: string) => {
    console.log('[AuthContext] Attempting to verify action code:', code)
    try {
      await applyActionCode(auth, code)
      return true
    } catch (error) {
      console.error('[AuthContext] Error verifying action code:', error)
      return false
    }
  }

  const handleVerifyPasswordResetCode = async (code: string) => {
    console.log('[AuthContext] Attempting to verify password reset code')
    try {
      const userEmail = await verifyPasswordResetCode(auth, code)
      return userEmail
    } catch (error) {
      console.error('[AuthContext] Error verifying password reset code:', error)
      throw error
    }
  }

  const handleSendPasswordResetEmail = async (email: string): Promise<void> => {
    const actionCodeSettings = {
      url: 'http://localhost:3000',
      handleCodeInApp: false,
    }

    try {
      await sendPasswordResetEmail(auth, email, actionCodeSettings)
    } catch (error) {
      console.error('Error sending password reset email:', error)
    }
  }

  const handleConfirmPasswordReset = async (code: string, newPassword: string) => {
    try {
      await confirmPasswordReset(auth, code, newPassword)
      return true
    } catch (error) {
      console.error('Error confirming password reset:', error)
      return false
    }
  }

  const handleSignInWithEmailLink = async (code: string) => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const email = urlParams.get('email')

      if (!email) {
        return false
      }

      let signInLink = window.location.href.split('&email')[0]
      console.log('signInLink', signInLink)
      signInLink = signInLink.replace('http://localhost:3000', 'https://stg-portal.optictruckworks.com')
      console.log('signInLink', signInLink)
      await signInWithEmailLink(auth, email, signInLink)
      return true
    } catch (error) {
      console.error('Error signing in with email link:', error)
      return false
    }
  }

  const checkExpired = async (user: User): Promise<boolean> => {
    try {
      const tokenResult = await user.getIdTokenResult(true)
      const issuedAt = tokenResult.claims.auth_time
      if (!issuedAt) {
        console.error('No issuedAt found')
        setRemainingSessionTime(null)
        return false
      }

      const issuedAtInt = parseInt(String(issuedAt), 10) * 1000
      const expiresAt = issuedAtInt + EXPIRATION_TIME_MS
      const currentTime = Date.now()

      if (currentTime > expiresAt) {
        setRemainingSessionTime(null)
        await logout()
        window.location.href = '/session-expired'
        return true
      }

      setRemainingSessionTime(expiresAt - currentTime)
      return false
    } catch (error) {
      console.error('Error checking session expiry:', error)
      setRemainingSessionTime(null)
      return false
    }
  }

  const refreshUserInfo = async (user: User) => {
    if (!user) return

    const expired = await checkExpired(user)
    if (expired) return

    const docSnapData = await getAuthUserProfile(user)

    const verified = user.emailVerified
    const data = { ...docSnapData, email_verified: verified }

    if (docSnapData) {
      setUserInfo((prev: PrivateUserInfoType) => ({
        ...prev,
        ...data,
      }))
    }
  }

  useEffect(() => {
    let cancelled = false
    let unsubscribe = () => {}

    const attachAuthListener = () => {
      unsubscribe = onAuthStateChanged(auth, async user => {
        setIsLoading(true)
        setCurrentUser(user)
        try {
          if (user) {
            if (user.email) {
              HyperDX.setGlobalAttributes({
                userId: user.uid,
                userEmail: user.email,
              })
            }
            await refreshUserInfo(user)
          } else {
            setUserInfo(null)
            setRemainingSessionTime(null)
          }
        } catch (error) {
          console.error('Auth state change error:', error)
          setUserInfo(null)
          setRemainingSessionTime(null)
        } finally {
          writeAuthSessionHint(Boolean(user))
          setIsLoading(false)
        }
      })
    }

    setIsLoading(true)
    void completeGoogleRedirectIfPresent(setUserInfo)
      .catch(error => {
        if (!cancelled) setGoogleAuthError(error)
      })
      .finally(() => {
        if (!cancelled) attachAuthListener()
      })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userInfo,
        refreshUserInfo,
        isLoading,
        setIsLoading,
        loginWithEmail,
        loginWithGoogle,
        googleAuthError,
        logout,
        handleResendVerificationEmail,
        handleSendPasswordResetEmail,
        handleVerifyPasswordResetCode,
        handleConfirmPasswordReset,
        handleVerifyActionCode,
        handleSignInWithEmailLink,
        remainingSessionTime,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }

// Export the hook separately for better usage
export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
